<template>
  <div class="content">
    <div class="page-inner">
      <div class="page-header mb-0"  v-if="!fromMetadataPage">
        <router-link to="/distributions">
          <i class="fa fa-chevron-left mr-1"></i>
          {{ $t('my_releases') }}
        </router-link>
      </div>
      <div class="text-right" v-else>
        <button type="button" class="btn btn-sm gray" @click="revertMetadata()" v-if="fromMetadataPage" :disabled="!enableMetadata || loadingMetadata">
          <i class="fas fa-redo-alt"></i> {{ $t('revert_changes') }}
          <span v-if="loadingMetadata">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
        </button>
        <button type="button" class="btn btn-sm btn-primary" @click="changeMetadata()" v-if="fromMetadataPage" :disabled="!Object.values(metadataChangeClass).filter(elem => elem).length || loadingMetadata">
          <i class="fas fa-save"></i> {{ $t('save_changes') }}
          <span v-if="loadingMetadata">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
        </button>
      </div>
      <DistributionProgress :distribution="distribution" v-if="!fromMetadataPage"></DistributionProgress>
      <div class="row">
        <div class="col-md-12">
          <div class="alert alert-warning mb-4">
            <i class="fa fa-exclamation-triangle text-warning mr-1"></i> <span v-html="$t('ai_content_notice')"></span>
          </div>
          <div v-if="!loading" :class="{'card': !fromMetadataPage}">
            <div class="card-header" v-if="!fromMetadataPage">
              <div class="card-head-row card-tools-still-right">
                <div class="card-title" v-if="!distribution._id">{{ $t('new_distribution') }}</div>
                <div class="card-title" v-else>{{ distribution.title }}</div>
                <div class="card-category d-none d-lg-block d-xl-none" v-if="!fromMetadataPage">{{ $t('fill_all_required') }}.</div>
                <div class="card-tools">
                  <button @click="removeRelease()" class="btn btn-light btn-border btn-sm" v-if="distribution && distribution._id && !distribution.status">
                    <span class="fa fa-trash text-danger mr-1"></span> {{ $t('remove') }}
                  </button>
                </div>
              </div>
            </div>
            <form @submit.prevent="saveDistribution()" id="save-distribution-form">
              <div class="card-body">
                <div class="row mb-4">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>
                        {{ $t('release_type') }}
                        <span class="required-label"></span>
                      </label>
                      <div>
                        <div class="input-group">
                          <input type="text" v-model="distribution.release_type" class="text-uppercase form-control" disabled="true" v-if="fromMetadataPage">
                          <ul class="nav nav-pills nav-primary" v-if="!fromMetadataPage">
                            <li class="nav-item">
                              <a
                                :class="{ active: distribution.release_type === 'single' }"
                                class="nav-link my-0"
                                href="javascript:void(0)"
                                @click="distribution.release_type = 'single'"
                              >Single (1 track)</a>
                            </li>
                            <li class="nav-item">
                              <a
                                :class="{ active: distribution.release_type === 'album' }"
                                class="nav-link my-0"
                                href="javascript:void(0)"
                                @click="distribution.release_type = 'album'"
                              >Album / EP</a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-8">
                    <div class="form-group">
                      <div class="alert-minimalist mt-alert" role="alert">
                        <div v-show="distribution.release_type === 'single'" class="animated fadeIn"><i class="flaticon-exclamation mr-2"></i>{{ (lang === 'es') ? 'Lanzamiento con una canción' : 'Release with 1 song'}}</div>
                        <div v-show="distribution.release_type === 'album'" class="animated fadeIn"><i class="flaticon-exclamation mr-2"></i>{{ (lang === 'es') ? 'Lanzamiento con 2 canciones o más' : 'Release with 2 or more songs'}}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mb-4">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>
                        {{ $t('artist') }}
                        <span class="required-label">*</span>
                      </label>
                      <router-link type="button" class="btn btn-default btn-block" to="/new-artist?distribution=true" v-if="!artists.length">
                        {{ $t('create_new_artist') }}
                      </router-link>
                      <div class="input-group" v-show="artists.length">
                        <div :class="{ 'container-adptable': !loading && !fromMetadataPage && isPro, 'container-full': !(!loading && !fromMetadataPage && isPro)}">
                          <Select2 v-model="distribution.artist" :options="artists" :disabled="fromMetadataPage"></Select2>
                        </div>
                        <div class="input-group-append new-artist" v-if="!loading && !fromMetadataPage && isPro">
                          <router-link to="/new-artist?distribution=true" class="btn btn-primary text-capitalize">
                            <span class="hide-mobileD">{{ $t('new') }}</span>
                            <span class="show-mobileD">{{ $t('create_new_artist') }}</span>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group" :class="{ 'has-error': showEmptyInputError && !distribution.language }">
                      <label for="language">
                        {{ $t('title_language') }}
                        <span class="required-label">*</span>
                      </label>
                      <div>
                        <div class="select2-input" :class="metadataChangeClass['language'] ? 'metadata-modified' : ''">
                          <Select2 v-model="distribution.language" :options="productLanguages"></Select2>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group" :class="{ 'has-error': invalidTitle }">
                      <label for="distribution-name">
                        {{ $t('release_title') }}
                        <span class="required-label">*</span>
                      </label>
                      <div :class="metadataChangeClass['title'] ? 'metadata-modified' : ''">
                        <input
                          type="text"
                          v-model="distribution.title"
                          class="form-control"
                          id="distribution-name"
                          name="name"
                          required
                          @change="validateDistroTitle()"
                          @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                          @paste="$event.preventDefault()"
                          @input="handleInput('release_title')"
                          :disabled="!distribution.language"
                        />
                        <small class="form-text text-danger" v-if="invalidTitle">{{ $t('do_not_use_emojis') }}</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group" :class="{ 'has-error': showEmptyInputError && !distribution.main_genre }">
                      <label>
                        {{ $t('main_genre') }}
                        <span class="required-label">*</span>
                      </label>
                      <div>
                        <div class="select2-input" :class="metadataChangeClass['main_genre'] ? 'metadata-modified' : ''">
                          <Select2 v-model="distribution.main_genre" :options="genres"></Select2>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group" :class="{ 'has-error': showEmptyInputError && !distribution.style }">
                      <label>
                        {{ $t('style') }}
                        <span class="required-label">*</span>
                      </label>
                      <div>
                        <div class="select2-input" :class="metadataChangeClass['style'] ? 'metadata-modified' : ''">
                          <Select2 v-model="distribution.style" :options="genresSource[distribution.main_genre]"></Select2>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group" v-if="user.custom_label">
                      <label>{{ $t('catalog_number') }}</label>
                      <div>
                        <input type="text" class="form-control" v-model="distribution.catalog_id" placeholder="(automatic)"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body bg-2 spotify-container">
                <div class="row">
                  <div class="col-md-4">
                    <span class="text-small text-muted ml-2">{{ $t('spotify_profile') }}</span>
                    <div class="form-group">
                      <label class="pr-2">{{ $t('already_have_spotify') }}</label> <br>
                      <div class="enable-spotify">
                        <div class="mask" v-if="fromMetadataPage"></div>
                        <div @click="spotifyArtistToggle()" class="display-inline">
                          <input type="checkbox" ref="spotifyArtist" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4 mt-2">
                    <div class="card mb-0 pos-relative already-spotify animated fadeInLeft" v-show="alreadyHaveSpotify">
                      <div class="card-body pt-2 pb-2">
                        <div v-show="!spotifyProfile" class="mt-2 mb-2 pt-3 pb-3 pl-2 pr-2 text-center profile-selector" @click="searchSpotifyArtist()">
                          <b>{{ $t('select_profile') }}</b>
                        </div>
                        <div class="card-list p-0 mt-2" v-if="spotifyProfile">
                          <div class="item-list p-0">
                            <div class="avatar-sm">
                              <a :href="spotifyProfile.external_urls.spotify" target="_blank" v-if="spotifyProfile.external_urls">
                                <img :src="(spotifyProfile && spotifyProfile.images.length > 1 && spotifyProfile.images[1].url) ? spotifyProfile.images[1].url : defaultImage" :alt="spotifyProfile.name" class="avatar-img rounded-circle">
                              </a>
                              <img class="avatar-img rounded-circle" :src="`${currentArtist.cover_image_small || '/static/img/img-placeholder.jpg'}`" alt="New Artist" v-if="!spotifyProfile.external_urls">
                            </div>
                            <div class="info-user mt-2 ml-3">
                              <div class="h4 mb-0">
                                <a class="title-artist" :href="spotifyProfile.external_urls.spotify" target="_blank" v-if="spotifyProfile.external_urls && !artist.spotify_id">
                                  {{ spotifyProfile.name }}
                                </a>
                                <span class="title-artist" v-if="!spotifyProfile.external_urls  && !artist.spotify_id">
                                  {{ currentArtist.text }}
                                </span>
                                <span v-if="artist.spotify_id" class="text-truncate" style="display:inline-block; max-width:180px; vertical-align:bottom;">
                                  {{ $t('spotify_artist_id') }}: {{ artist.spotify_id.slice(0, 16) + '...' }}
                                </span>
                              </div>
                               <div v-if="artist.spotify_id" class="h5 text-muted followers">
                                <a
                                  :href="`https://open.spotify.com/artist/${artist.spotify_id.replace('spotify:artist:', '')}`"
                                  target="_blank"
                                >{{ $t('view_spotify') }}</a>
                              </div>
                              <div class="h5 text-muted followers" v-if="spotifyProfile.followers && !artist.spotify_id">{{ spotifyProfile.followers.total }} {{ $t('followers') }}</div>
                              <div class="h5 text-muted followers" v-else>&nbsp;</div>
                            </div>
                            <span class="btn btn-sm btn-gray remove-contributor-icon" @click="removeSpotifyProfile()" v-if="!fromMetadataPage">
                              <i class="fas fa-times"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="alert alert-warning mt-2 padding-right-custom" v-show="endYearSuspend">
                  <label v-html="$t('distros_suspend_info')"></label>
                </div>
                <div>
                  <span class="text-small text-muted ml-2">{{ $t('release_options') }}</span>
                </div>
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label for="">{{ $t('previously_published') }}</label>
                      <span class="tooltip-label" v-tooltip.hover.focus.click="{ ref: 'tooltipRefReleaseOptions', class: 'tooltip-whatisit' }">{{ $t('whats_this') }}</span>
                      <div>
                        <div @click="setPrevPublish()" class="display-inline">
                          <input type="checkbox" id="prevPublish" data-toggle="toggle" data-onstyle="primary" data-style="btn-round" class="toggle-checkbox" data-size="small">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4" v-show="prevPublish">
                    <div class="form-group">
                      <label>
                        {{ $t('original_release_date') }}
                        <span class="required-label"></span>
                      </label>
                      <div>
                        <div class="input-group" :class="(hasErrorOriginalDate) ? 'has-error' : ''">
                          <input
                            type="text"
                            id="release-date-back"
                            class="form-control"
                            placeholder
                            name="release_date_back"
                            :disabled="fromMetadataPage"
                          />
                          <div class="input-group-append">
                            <span class="input-group-text">
                              <i class="fa fa-calendar"></i>
                            </span>
                          </div>
                        </div>
                        <div class="alert alert-warning mt-3 mb-1 py-2" v-if="!isPro">
                          <div>{{ $t('want_publish_faster') }}</div>
                          <div class="mt-1">
                            <router-link :to="`/upgrade?from=faster_release_date&next=/distributions`" class="btn btn-primary btn-sm">{{ $t('upgrade_now') }}</router-link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group" @click="preventReleaseChange($event)">
                      <label>
                        {{ $t('release_date') }} <span class="badge bg-primary text-white">PRO</span>
                        <span class="required-label"></span>
                      </label>
                      <div>
                        <div class="input-group" :class="(hasErrorDate) ? 'has-error' : ''">
                          <input
                            type="text"
                            id="release-date"
                            class="form-control"
                            placeholder
                            name="release_date"
                            :disabled="fromMetadataPage"
                          />
                          <div class="input-group-append">
                            <span class="input-group-text">
                              <i class="fa fa-calendar"></i>
                            </span>
                          </div>
                        </div>
                        <div class="alert alert-warning mt-3 mb-1 py-2" v-if="!isPro && user.published">
                          <div>
                            {{ $t('want_publish_faster') }}
                          </div>
                          <div class="mt-1">
                            <router-link :to="`/upgrade?from=faster_release_date&next=/distributions`" class="btn btn-primary btn-sm">{{ $t('upgrade_now') }}</router-link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4" v-show="prevPublish"></div>
                  <div class="col-md-4" v-show="prevPublish">
                    <div class="form-group">
                      <label for>UPC
                      <span class="tooltip-label" v-tooltip.hover.focus.click="{ ref: 'tooltipRefUPC', class: 'tooltip-whatisit' }">{{ $t('whats_this') }}</span></label>
                      <input
                        type="text"
                        class="form-control"
                        :class="{'has-error': hasErrorUpc, 'metadata-modified': metadataChangeClass['upc']}"
                        v-model="distribution.upc"
                        maxlength="14"
                        minlength="12"
                        :placeholder="$t('optional')"
                        @drop="$event.preventDefault()" @dragenter.prevent @dragover.prevent
                        :disabled="distribution.reopening_reason"
                        v-on:keypress="checkIntroKeyInput($event)"
                        @blur="findUpcExist()"
                      />
                    </div>
                    <div ref="tooltipRefReleaseOptions" class="tooltip-content">
                      <h4>{{ $t('enable_back_catalog') }}</h4>
                      <p>{{ $t('back_catalog_description') }}</p>
                    </div>
                    <div ref="tooltipRefUPC" class="tooltip-content">
                      <h4>Universal Product Code (UPC)</h4>
                      <p v-html="(lang === 'es') ? 'Código numérico único para representar y trackear tu música, es otorgado por las distribuidoras. Necesario para el back-catalog.' : 'A specific numeric code to represent and track your <b>music</b>, provided by your previous distributor. Necessary for back-catalog.'"></p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body bg-2 container-service" v-if="activeService._id">
                <fieldset>
                  <legend class="text-14">
                    <span class="title">{{ $t('selected_service_details') }}</span>
                    <button class="btn btn-danger btn-sm float-right" @click="deleteService(activeService._id)" :disabled="saving">
                      <i class="fa fa-trash" v-if="!saving"></i>
                      <i class="fa fa-spinner fa-spin" v-if="saving"></i>
                    </button>
                  </legend>
                  <div class="row mb-4">
                    <div class="col-md-12">
                      <div class="separator-solid"></div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>{{ $t('name') }}</label>
                        <div>{{ activeService.service_type.name }}</div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>{{ $t('payment_date') }}</label>
                        <div>{{ moment(activeService.paidAt).format('LLL') }}</div>
                      </div>
                    </div>
                    <div class="col-md-12">
                      <div class="separator-solid"></div>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div class="card-action" v-if="!fromMetadataPage">
                <div class="row">
                  <div class="col-md-12 text-right">
                    <router-link
                      to="/distributions"
                      tag="button"
                      class="btn btn-border no-border"
                      :disabled="saving"
                    >
                      <i class="fa fa-chevron-left mr-1"></i> {{ $t('back') }}
                    </router-link>
                    <button class="btn btn-gradient ml-1" type="submit" :disabled="saving">
                      <span v-if="!saving">
                        {{ $t('continue') }}
                        <i class="fa fa-chevron-right ml-1"></i>
                      </span>
                      <span v-if="saving">
                        <i class="fa fa-spinner fa-spin"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- <div v-if="!user.subscription" class="text-center mb-3 mt-3">
        <ins class="adsbygoogle"
            style="display:block"
            data-ad-client="ca-pub-8326584585992829"
            data-ad-slot="3934111543"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
      </div> -->

      <div ref="distroTitleInstructionsModal" class="modal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
        <div class="modal-dialog modal-md" role="document">
          <div class="modal-content">
            <div class="modal-body">
              <div class="h1 text-center">
                <span class="flaticon-exclamation text-warning title-instructions-modal"></span>
              </div>
              <div class="text-center mb-4">
                <h1>{{ $t('title_requirements') }}</h1>
              </div>
              <div class="mb-3">{{ $t('do_not_use_emojis_modal') }}</div>
              <div class="row">
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.upperCase" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-times-circle text-danger"></i>
                  </span>
                </div>
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.lowerCase" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-times-circle text-danger"></i>
                  </span>
                </div>
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.mixUpperAndLowerCase" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-times-circle text-danger"></i>
                  </span>
                </div>
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.parenthesisUpperCase" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-times-circle text-danger"></i>
                  </span>
                </div>
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.parenthesisLowerCase" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-times-circle text-danger"></i>
                  </span>
                </div>
                <div class="col-9 col-md-8 mb-2">
                  <input type="text" class="form-control" :value="titleRequisites.validTitle" readonly>
                </div>
                <div class="col-1 col-md-4 text-center">
                  <span class="h1">
                    <i class="far fa-check-circle text-success"></i>
                  </span>
                </div>
              </div>
              <div class="mt-3">{{ $t('do_not_use_emojis_modal_2') }}</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-success" data-dismiss="modal" @click="hideTitleInstructionsModal()">{{ $t('understood') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div ref="spotifyModal" class="modal spotifyModal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header pt-4 pb-4">
            <h4 class="modal-title">{{ $t('select_spotify_profile') }}</h4>
          </div>
          <div class="modal-body pr-5 pl-5">
            <p class="text-center p-4" v-if="loadingSpotifyArtists">
              <i class="fa fa-spinner fa-spin text-muted"></i>
            </p>
            <p class="text-center p-4" v-if="!loadingSpotifyArtists && !spotifyArtists.length">
              {{ $t('no_spotify_artists_found') }}
            </p>
            <div class="card-list">
              <div class="item-list" v-for="artist in spotifyArtists" v-bind:key="artist.id">
                <div class="avatar-sm">
                  <img :src="artist.images[0] ? artist.images[0].url : defaultImage" :alt="artist.name" onerror="this.src = '/static/img/img-placeholder.jpg'" class="avatar-img rounded-circle">
                </div>
                <div class="info-user ml-3">
                  <a :href="artist.external_urls.spotify" target="_blank" class="username" v-if="artist.external_urls">
                    {{ artist.name }}
                    <i class="fas fa-external-link-alt"></i>
                  </a>
                  <span class="title-artist" v-if="!artist.external_urls">
                    {{ artist.name }}
                  </span>
                  <div class="status">{{ artist.followers.total }} {{ $t('followers') }}</div>
                </div>
                <button class="btn btn-primary btn-xs spotify-select" @click="selectSpotifyArtist(artist)">
                  {{ $t('select') }}
                </button>
              </div>
            </div>
            <div class="spotify-paginator mt-4" v-if="spotifyArtists.length">
              <div class="paginator previous">
                <button class="btn" @click="searchSpotifyArtist(spotifyPrev)" :disabled="!spotifyPrev">
                  <i class="fas fa-angle-double-left"></i>
                  {{ $t('previous') }}
                </button>
              </div>
              <div class="paginator next">
                <button class="btn" @click="searchSpotifyArtist(spotifyNext)" :disabled="!spotifyNext">
                  {{ $t('next') }}
                  <i class="fas fa-angle-double-right"></i>
                </button>
              </div>
            </div>
            <div class="text-center mt-3">
              <button
                v-if="!showManualSpotifyId"
                class="btn btn-link"
                @click="showManualSpotifyId = true"
              >
                {{ $t('can_not_your_profile_spotify') }}
              </button>
            </div>
            <div v-if="showManualSpotifyId" class="mt-3 p-3 border rounded bg-light">
              <label for="manual-spotify-id" class="font-weight-bold mb-1">
                {{ $t('spotify_artist_id_url') }}
                <span class="text-muted" style="font-size: 12px;">
                  (e.g. <code>spotify:artist:7yaahg4SFaLODTEYxnIlhS</code> or <code>https://open.spotify.com/artist/7yaahg4SFaLODTEYxnIlhS</code>)
                </span>
              </label>
              <input
                id="manual-spotify-id"
                v-model="manualSpotifyId"
                class="form-control"
                :placeholder="$t('paste_spotify_artist_id_url')"
                @input="manualSpotifyIdError = ''; manualSpotifyIdSuccess = ''"
                autocomplete="off"
              />
              <div class="d-flex mt-2">
                <button
                  class="btn btn-primary flex-grow-1 mr-2"
                  @click="submitManualSpotifyId"
                >{{ $t('save_spotify_id') }}</button>
                <button
                  class="btn btn-secondary flex-grow-1"
                  @click="cancelManualSpotifyId"
                >{{ $t('cancel') }}</button>
              </div>
              <div v-if="manualSpotifyIdError" class="alert alert-danger mt-2 py-1 px-2">{{ $t(manualSpotifyIdError) }}</div>
              <div v-if="manualSpotifyIdSuccess" class="alert alert-success mt-2 py-1 px-2">{{ $t(manualSpotifyIdSuccess) }}</div>
            </div>
          </div>
          <div class="modal-footer text-center pt-5">
            <button class="btn mr-2 btn-default btn-border btn-cancel" data-dismiss="modal">{{ $t('close') }}</button>
            <button type="button" class="btn btn-primary" data-dismiss="modal" @click="createSpotifyProfile()">
              {{ $t('create_new') }}
            </button>
          </div>
          <div class="modal-body pr-5 pl-5 pb-4 text-center bottom" v-html="$t('spotify_footer_description')"></div>
        </div>
      </div>
    </div>
    <div ref="extraDistroServicesModal" class="modal fade" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-lg" style="max-width: 800px !important;">
        <div class="modal-content">
          <div class="modal-header pt-4 pb-4">
            <h4 class="modal-title">{{ $t('on_demand_services') }}</h4>
          </div>
          <div class="modal-body">
            <div>{{ $t('services_available_extra_distro') }}</div>
            <div class="card-list">
              <table class="table mt-1 services-table">
                <thead>
                  <tr>
                    <th>{{ $t('services') }}</th>
                    <th>{{ $t('payment_method') }}</th>
                    <th>{{ $t('payment_date') }}</th>
                    <th>{{ $t('total') }}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(service, index) in extraDistroServices" v-bind:key="`service_${index}`">
                    <td>{{ service.service_type.name }}</td>
                    <td class="payment-method">{{ service.payment_method }}</td>
                    <td><span v-if="service.paidAt">{{ moment(service.paidAt).format('LL') }}</span></td>
                    <td><span v-if="service.total">{{ currency.format(service.total) }}</span></td>
                    <td>
                      <button class="btn btn-primary btn-sm" @click="selectService(service)" :disabled="saving">
                        <span>{{ $t('select') }}</span>
                        <i class="fa fa-spinner fa-spin" v-if="saving"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer text-center pt-5">
            <button class="btn mr-2 btn-default btn-border btn-cancel" data-dismiss="modal" @click="closeExtraDistroServicesModal()">{{ $t('close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <div ref="limitedTracksModal" class="modal" tabindex="-1" role="dialog" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-body m-2">
            <div class="card card-pricing mb-0">
              <div class="card-header">
                <h4 class="strong">{{ $t('you_have_reached_track_limit') }}</h4>
                <div class="card-price mt-4">
                  <span class="bold h1">Indiefy</span>
                  <span class="h1 fw-light">PRO</span>
                </div>
              </div>
              <div class="card-body">
                <ul class="specification-list">
                  <li>
                    <span class="strong">{{ $t('premium_stores') }}</span>
                    <!-- <span class="status-specification muted">{{ $t('pro_releases_month') }}</span> -->
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_projects') }}</span>
                    <span class="status-specification muted">{{ $t('pro_artists_or_bands') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('number_of_tracks') }}</span>
                    <span class="status-specification muted">{{ $t('track_limit_pro') }}</span>
                  </li>
                  <li>
                    <span class="strong">{{ $t('deliveries') }}</span>
                    <span class="status-specification muted">{{ $t('express') }}</span>
                  </li>
                  <div class="text-left mt-2 small">
                    <router-link to="/upgrade?from=track_limit_editor" class="strong">{{ $t('view_all_benefits') }}</router-link>
                  </div>
                </ul>
                <h1 class="text-center mt-4 text-primary strong my-5">
                  $<span class="price-big">4.16</span>/mo
                </h1>

                <div class="row">
                  <div class="col-md-6">
                    <button type="button" class="btn btn-dark w-100" data-dismiss="modal">{{ $t('maybe_later') }}</button>
                  </div>
                  <div class="col-md-6">
                    <router-link tag="button" :to="`/upgrade?from=track_limit_editor`" class="btn btn-primary w-100">{{ $t('upgrade_now') }}</router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <UpgradeModal></UpgradeModal>
  </div>
</template>
<script>
import _ from 'underscore'
import Vue from 'vue'
import Distributions from '../services/distributions'
import Artists from '../services/artists'
import Labels from '../services/labels'
import Select2 from './utils/Select2'
import genres from '../data/genres'
import productLanguages from '../data/product-languages'
import filters from './utils/filters'
import moment from 'moment'
import User from '../services/user'
import performanceLanguages from '../data/performance-languages'
import barcoder from 'barcoder'
import Upc from '../services/upc'
import DistributionProgress from './utils/DistributionProgress'
import Configuration from '../services/configuration'
import ServicesOnDemand from '../services/services-on-demand'
import Public from '../services/public'
import BLACKLIST_WORDS from '../data/blacklistwords'
import LANGUAGE_REGEX from '../data/productlanguagesregex'
import UpgradeModal from './utils/UpgradeModal'

export default {
  name: 'DistributionEditor',
  props: ['fromMetadataPage'],
  data: function () {
    return {
      name: 'DistributionEditor',
      loadingSpotifyArtists: false,
      indiefyArtists: [],
      spotifyArtists: [],
      locationSettings: {},
      alreadyHaveSpotify: false,
      spotifyProfile: null,
      isPro: true,
      loading: true,
      labels: [],
      lang: '',
      visibility: true,
      capitalized: false,
      invalidTitle: false,
      showTitleInstructionsModal: true,
      prevPublish: false,
      localtionSettings: {},
      user: {},
      saving: false,
      artists: [],
      productLanguages: _.map(productLanguages, lang => {
        return { id: lang.value, text: lang.name }
      }),
      performanceLanguages: _.map(performanceLanguages, item => {
        return { id: item.value, text: item.name }
      }),
      genresSource: genres,
      genres: _.map(genres, (genre, key) => {
        return key
      }),
      distribution: {
        source: 'web_v2',
        artist: '',
        custom_label: null,
        language: '',
        main_genre: '',
        songs: [{ title: '' }],
        release_type: 'single',
        release_date: moment()
          .utc()
          .add(21, 'days')
          .toDate()
          .toISOString(),
        original_release_date: moment()
          .utc()
          .add(21, 'days')
          .toDate()
          .toISOString()
      },
      distributionOriginal: {},
      artistOriginal: {},
      showEmptyInputError: false,
      upperAndLowerText: '',
      titleRequisites: {
        upperCase: '',
        lowerCase: '',
        mixUpperAndLowerCase: '',
        parenthesisUpperCase: '',
        parenthesisLowerCase: '',
        validTitle: ''
      },
      currentArtist: {},
      strikesCount: 0,
      hasErrorDate: false,
      hasErrorOriginalDate: false,
      defaultImage: '/static/img/spotify-logo-big.png',
      hasErrorUpc: false,
      serverDefaultDate: moment().utc(),
      endYearSuspend: null,
      riseLimitDistro: null,
      reopeningOptions: [],
      metadataRequestData: {},
      metadataChangeClass: {
        title: false,
        language: false,
        upc: false,
        main_genre: false,
        style: false
      },
      extraDistroServices: [],
      moment: moment,
      activeService: {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      },
      showManualSpotifyId: false,
      manualSpotifyId: '',
      manualSpotifyIdError: '',
      manualSpotifyIdSuccess: '',
      currency: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
      spotifyPrev: null,
      spotifyNext: null,
      enableMetadata: false,
      loadingMetadata: false,
      distroBannedWords: [],
      artist: {
        spotify_id: ''
      }
    }
  },
  methods: {
    preventReleaseChange: function () {
      if (!this.user.subscription || (this.user.subscription && this.user.subscription.plan_name === 'indiefy_lite')) {
        this.$eventBus.$emit('showUpgradeModal', '', 'distribution')
      }
    },
    initPickers: function (serverDefaultDate) {
      setTimeout(() => {
        let minReleaseDate = moment(serverDefaultDate).utc().startOf('day').add(21, 'days')
        // TODO: Update again this part - after January 5th - update from 2 to 1
        let maxReleaseDate = moment(serverDefaultDate).utc().startOf('day').add(2, 'months')
        let defaultDate = minReleaseDate
        let isProUser = false

        if (this.user.subscription && this.user.subscription.plan_name !== 'indiefy_lite') {
          isProUser = true
        }

        if (isProUser) { // only PRO
          minReleaseDate = moment(serverDefaultDate).utc().startOf('day').add(7, 'days')
          maxReleaseDate = moment(serverDefaultDate).utc().startOf('day').add(6, 'months')
        }

        minReleaseDate = moment(this.getMinReleaseDate(isProUser)).utc()

        if (this.distribution._id) {
          defaultDate = moment(this.distribution.release_date).utc().startOf('day')
        } else {
          defaultDate = minReleaseDate
        }
        if (this.endYearSuspend) {
          minReleaseDate = moment(serverDefaultDate).utc().endOf('year')
          defaultDate = minReleaseDate
        }
        if (!this.prevPublish) {
          this.distribution.release_date = defaultDate.toISOString()
        }

        this.datepicker = window.$('#release-date').datetimepicker({
          minDate: minReleaseDate,
          maxDate: maxReleaseDate,
          date: moment(defaultDate).utc(),
          format: 'LL'
        })

        this.datepicker.on('dp.change', item => {
          if (item && item.date) {
            Vue.set(this.distribution, 'release_date', item.date.utc().startOf('day').toDate().toISOString())
          } else {
            console.error('Date not present', item)
          }
        })

        minReleaseDate = moment(serverDefaultDate).utc().add(-50, 'years')
        maxReleaseDate = moment(serverDefaultDate).utc()

        if (this.distribution._id && this.prevPublish) {
          defaultDate = moment(this.distribution.original_release_date).utc()
        } else {
          defaultDate = maxReleaseDate
        }

        this.datepickerBack = window.$('#release-date-back').datetimepicker({
          minDate: minReleaseDate,
          maxDate: maxReleaseDate,
          date: moment(defaultDate).utc(),
          format: 'LL'
        })

        this.datepickerBack.on('dp.change', item => {
          if (item && item.date && this.prevPublish) {
            Vue.set(this.distribution, 'original_release_date', item.date.utc().startOf('day').toDate().toISOString())
          } else {
            console.error('Date not present', item)
          }
        })

        setTimeout(() => {
          let minReleaseDate = (this.user.subscription) ? moment(this.serverDefaultDate).utc().startOf('day').add(7, 'days') : moment(this.serverDefaultDate).utc().startOf('day').add(21, 'days')

          if (moment(this.distribution.release_date).utc().startOf('day').isBefore(minReleaseDate)) {
            Vue.set(this.distribution, 'release_date', minReleaseDate.toDate().toISOString())
          }

          if (!this.user.subscription || (this.user.subscription && this.user.subscription.plan_name === 'indiefy_lite')) {
            this.datepicker.data('DateTimePicker').disable()
          }
        }, 500)
      }, 200)
    },
    getMinReleaseDate: function (isProUser) {
      const currentDate = new Date() // Get the current date
      const currentMonth = currentDate.getMonth() // Get the current month (0: January, 11: December)
      const currentYear = currentDate.getFullYear() // Get the current year

      // Define the minimum release date for December (January 5th)
      let minReleaseDate

      if (currentMonth === 11) { // If it's December
        minReleaseDate = new Date(currentYear + 1, 0, 5) // Set minimum release date to January 5th of the next year

        // If the user is trying to release in December, adjust based on minimum days
        if (isProUser) {
          // For a PRO user, the minimum is 7 days in advance
          if (currentDate.getDate() > 24) { // If it's after December 24th
            minReleaseDate = new Date(currentYear + 1, 0, 5) // Set release date to January 5th
          }
        } else {
          // For a BASIC user, the minimum is 21 days in advance
          if (currentDate.getDate() > 4) { // If it's after January 4th
            minReleaseDate.setDate(minReleaseDate.getDate() + 21) // Add 21 days
          }
        }
      } else {
        // If it's not December, calculate based on the user type
        if (isProUser) {
          // For PRO user: minimum 7 days in advance
          minReleaseDate = new Date(currentDate)
          minReleaseDate.setDate(minReleaseDate.getDate() + 7) // Add 7 days
        } else {
          // For BASIC user: minimum 21 days in advance
          minReleaseDate = new Date(currentDate)
          minReleaseDate.setDate(minReleaseDate.getDate() + 21) // Add 21 days
        }
      }
      return minReleaseDate.toISOString().split('T')[0] // Return the date in YYYY-MM-DD format
    },
    checkIntroKeyInput: function (e) {
      let char = String.fromCharCode(e.keyCode)

      if (/^\d+$/g.test(char)) return true
      else e.preventDefault()
    },
    createSpotifyProfile: function () {
      this.spotifyProfile = {
        name: this.currentArtist.text,
        new: true
      }

      window.$(this.$refs.spotifyArtist).prop('checked', false).change()
      window.$(this.$refs.spotifyModal).modal('hide')
    },
    spotifyArtistToggle: function () {
      setTimeout(() => {
        if (!window.$(this.$refs.spotifyArtist).prop('checked')) {
          this.alreadyHaveSpotify = false
          this.spotifyProfile = null
        } else {
          if (this.spotifyProfile && !this.spotifyProfile.id) {
            this.spotifyProfile = null
          }

          this.alreadyHaveSpotify = true
        }

        this.$forceUpdate()
      }, 1)
    },
    setPrevPublish: function () {
      this.prevPublish = !window.$('#prevPublish').prop('checked')

      let originalReleaseDate = (this.prevPublish) ? this.distribution.original_release_date : this.distribution.release_date

      Vue.set(this.distribution, 'original_release_date', originalReleaseDate)
      Vue.set(this.distribution, 'previously_published', (this.prevPublish) ? 1 : 0)
    },
    showTitleInstructions: function () {
      if (this.showTitleInstructionsModal) {
        window.$(this.$refs.distroTitleInstructionsModal).modal('show')
      }
    },
    hideTitleInstructionsModal: function () {
      this.showTitleInstructionsModal = false
      window.$('#distribution-name').focus()
    },
    removeRelease: function () {
      var html = document.createElement('div')
      html.innerHTML = this.$t('want_to_remove_release') + '<br><br><b>' + this.distribution.title + '?</b>'
      window.swal({
        title: this.$t('delete_release'),
        content: html,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: this.$t('cancel'),
            visible: true,
            className: 'btn btn-default btn-border no-border'
          },
          confirm: {
            text: this.$t('remove'),
            className: 'btn btn-danger'
          }
        }
      }).then(confirm => {
        if (confirm) {
          Distributions.remove(this.distribution._id).then(response => {
            this.$notify({ message: this.$t('distribution_removed') }, 'success', 'fa-check')
            this.$router.push(`/distributions`)
          })
        }
      })
    },
    saveSpotifyArtist: function (callback) {
      let artist = _.find(this.indiefyArtists, item => item._id === this.currentArtist.id)
      artist.spotify_id = ''

      if (this.spotifyProfile && this.spotifyProfile.uri) {
        Artists.getSpotifyArtistIndiefy(this.spotifyProfile.uri.replace('spotify:artist:', '')).then(response => {
          if (!response.data.duplicated) {
            if (this.spotifyProfile.uri) {
              artist.spotify_id = this.spotifyProfile.uri
            } else {
              artist.create_spotify_profile = 1
            }
            Artists.save(artist).then(callback)
          } else {
            this.saving = false
            var html = document.createElement('div')
            html.setAttribute('style', 'padding:0 40px')
            html.innerHTML = '<br/>' + this.$t('duplicate_spotify_description') + '<br/><br/><br/>'
            window.swal({
              title: this.$t('attention'),
              content: html,
              closeOnEsc: false,
              closeOnClickOutside: false,
              buttons: {
                confirm: {
                  text: this.$t('i_understand'),
                  className: 'btn btn-primary'
                }
              }
            })
          }
        })
      } else {
        Artists.save(artist).then(callback)
      }
    },
    saveDistribution: function () {
      this.showEmptyInputError = true
      let originalReleaseDate = (this.prevPublish) ? this.distribution.original_release_date : this.distribution.release_date
      let minReleaseDate = (this.user.subscription) ? moment(this.serverDefaultDate).utc().startOf('day').add(7, 'days') : moment(this.serverDefaultDate).utc().startOf('day').add(21, 'days')

      if (moment(this.distribution.release_date).utc().startOf('day').isBefore(minReleaseDate)) {
        window.$('#release-date').val(moment(minReleaseDate).utc().format('LL'))
        Vue.set(this.distribution, 'release_date', minReleaseDate.toDate().toISOString())

        this.hasErrorDate = true
        setTimeout(() => {
          this.hasErrorDate = false
        }, 2000)
        return this.$notify({ message: this.$t('release_date_invalid') }, 'warning', 'fa-exclamation')
      }

      if (this.prevPublish) {
        if (moment(originalReleaseDate).utc().startOf('day').isAfter(moment(this.distribution.release_date).utc().startOf('day'))) {
          window.$('#release-date-back').val(moment(this.serverDefaultDate).utc().format('LL'))
          Vue.set(this.distribution, 'original_release_date', moment(this.serverDefaultDate).utc().startOf('day').toISOString())
          this.hasErrorOriginalDate = true
          setTimeout(() => {
            this.hasErrorOriginalDate = false
          }, 2000)
          return this.$notify({ message: this.$t('original_release_date_invalid') }, 'warning', 'fa-exclamation')
        }
      }
      Vue.set(this.distribution, 'original_release_date', originalReleaseDate)
      Vue.set(this.distribution, 'previously_published', (this.prevPublish) ? 1 : 0)

      this.distribution.upc = parseInt(this.distribution.upc)

      if (!this.prevPublish && this.distribution.upc) {
        this.distribution.upc = null
      } else if (this.distribution.upc && !Number.isInteger(this.distribution.upc)) {
        return this.$notify({ message: this.$t('upc_cannot_text') }, 'warning', 'fa-exclamation')
      }

      if (this.prevPublish && this.distribution.upc) {
        if (!barcoder.validate(String(this.distribution.upc))) {
          return this.$notify({ message: this.$t('upc_not_valid') }, 'warning', 'fa-exclamation')
        }
      }

      if (this.hasReleaseTypeInTitle()) {
        this.showInvalidWordsModal()
        return
      }

      if (this.validateBannedWords()) {
        this.invalidBannedWords()
        return
      }

      if (this.validateArtistInDistroTitle()) {
        this.invalidArtistInDistroTitle()
        return
      }

      if (this.distribution.title && this.hasSpecialCaractersInTitle()) {
        this.invalidTitle = true
        return this.$notify({ message: this.$t('special_characters_error') }, 'warning', 'fa-exclamation')
      }

      if (!this.distribution.artist) {
        return this.$notify(
          { message: this.$t('select_artist') },
          'warning',
          'fa-exclamation'
        )
      }

      if (!this.distribution.language) {
        return this.$notify({ message: this.$t('select_language') }, 'warning', 'fa-exclamation')
      }

      if (!this.distribution.title) {
        return this.$notify({ message: this.$t('select_a_release_title') }, 'warning', 'fa-exclamation')
      }

      if (!this.distribution.main_genre) {
        return this.$notify({ message: this.$t('main_genre_required') }, 'warning', 'fa-exclamation')
      }

      if (!this.distribution.style) {
        return this.$notify({ message: this.$t('style_required') }, 'warning', 'fa-exclamation')
      }

      if (this.alreadyHaveSpotify && !this.spotifyProfile) {
        return this.$notify({ message: this.$t('please_select_spotify_artist') }, 'warning', 'fa-exclamation')
      }

      this.saving = true

      if (!this.fromMetadataPage) {
        new Promise(resolve => {
          if (!this.distribution.service) {
            Distributions.getAvailableDistributions().then(response => {
              if (response.data.available <= 0) {
                // Show upgrade modal instead of OnDemand releases (temporary)
                this.saving = false
                return window.$(this.$refs.limitedTracksModal).modal('show')
              } else {
                resolve()
              }
            }).catch((error) => {
              console.error('Error while trying to get distribution limit (distribution editor)', error)
              this.availableDistributions = 1
            })
          } else {
            resolve()
          }
        }).then(() => {
          this.saveSpotifyArtist(() => {
            let distributionObject = _.clone(this.distribution)
            distributionObject.release_date = moment(distributionObject.release_date).utc().startOf('day').toISOString()

            if (typeof distributionObject.steps === 'undefined') {
              distributionObject.steps = {}
            }
            distributionObject.steps.step_release = { validate: true }
            distributionObject.fromPage = 'release'

            if (!this.user.subscription && distributionObject.songs && distributionObject.songs.length > 5) {
              distributionObject.songs = distributionObject.splice(0, 5)
            }

            Distributions.save(distributionObject).then(response => {
              this.saving = false
              this.$notify(
                { message: this.$t('distribution_updated') },
                'success',
                'fa fa-check'
              )

              if (!this.distribution._id) {
                window.twttr && window.twttr.conversion.trackPid('o2s4b', { tw_sale_amount: 0, tw_order_quantity: 0 })
                window.gtag && window.gtag('event', 'Created', {
                  hitType: 'event',
                  event_category: 'Distribution',
                  event_label: 'Distribution Created'
                })

                window.gtag && window.gtag('event', 'page_view', {
                  page_title: document.title,
                  page_location: 'https://indiefy.net/distribution-created',
                  page_path: '/distribution-created',
                  send_to: 'UA-80876238-1'
                })

                window.gtag && window.gtag('event', 'page_view', {
                  page_title: document.title,
                  page_location: 'https://indiefy.net/distribution-created',
                  page_path: '/distribution-created',
                  send_to: 'AW-877256789'
                })
              }
              if (this.distribution.reopen_in_progress) {
                this.$router.push(`/distributions/${response.data._id}/review`)
              } else {
                this.$router.push(`/distributions/${response.data._id}/songs`)
              }
            }).catch(err => {
              let error = err.response.data.name

              if (error === 'already_exists') {
                this.$notify(
                  { message: this.$t('distribution_name_repeated') },
                  'danger',
                  'fa-exclamation'
                )
              } else if (error === 'distro_limit') {
                this.$notify(
                  { message: this.riseLimitDistro ? this.$t('distro_limit_reached_2') : this.$t('distro_limit_reached') },
                  'danger',
                  'fa-exclamation'
                )
              } else if (error === 'creating_without_artist') {
                this.$notify(
                  { message: this.$t('creating_without_artist') },
                  'danger',
                  'fa-exclamation'
                )
              } else if (error === 'distro_already_reviewed') {
                if (this.$route.params.id) {
                  this.$router.push(`/distributions/${this.$route.params.id}/details`)
                } else {
                  this.$router.push(`/distributions`)
                }
              } else if (error === 'banned_words') {
                this.$notify(
                  { message: `${this.$t('distribution_process.banned_word_error_msg').replaceAll('BANNED_WORDS', this.distroBannedWords.join(', '))}` },
                  'danger',
                  'fa-exclamation'
                )
              } else if (error === 'basic_songs_limit_reached') {
                window.$(this.$refs.limitedTracksModal).modal('show')
              }

              this.saving = false
            })
          })
        })
      } else {
        this.loadingMetadata = true
        Distributions.saveMetadaRequest(this.distribution._id, this.name, this.metadataRequestData[this.name]).then(response => {
          this.$eventBus.$emit('notifyMetadataModified', {origin: this.name, data: this.metadataRequestData[this.name]})
          this.$notify({ message: this.$t('changes_saved') }, 'success', 'fa-check')
          this.enableMetadata = !!_.find(this.metadataRequestData[this.name], elem => elem)
          this.loadingMetadata = false
          this.$forceUpdate()
        })
      }
    },
    validateDistroTitle: function () {
      this.invalidTitle = false
      this.distribution.title = this.distribution.title.replace(/  +/g, ' ')

      if (this.distribution.title) {
        this.titleRequisites = {
          upperCase: this.distribution.title.toUpperCase(),
          lowerCase: this.distribution.title.toLowerCase(),
          mixUpperAndLowerCase: filters.mixUpperAndLowerCase(this.distribution.title),
          parenthesisUpperCase: `${this.distribution.title.toUpperCase()} (ORIGINAL)`,
          parenthesisLowerCase: `${this.distribution.title.toLowerCase()} (original)`
        }

        if (!this.distribution.title) {
          this.titleRequisites.validTitle = 'Valid Distribution Name'
        } else {
          this.titleRequisites.validTitle = filters.capitalizeText(this.distribution.title.toLowerCase())
        }

        if (this.hasReleaseTypeInTitle()) {
          this.invalidTitle = true
          this.showInvalidWordsModal()
          return
        }

        if (this.validateBannedWords()) {
          this.invalidTitle = true
          this.invalidBannedWords()
        }

        if (this.validateArtistInDistroTitle()) {
          this.invalidTitle = true
          this.invalidArtistInDistroTitle()
        }

        if (this.hasSpecialCaractersInTitle()) {
          this.invalidTitle = true
          return this.$notify({ message: this.$t('special_characters_error') }, 'warning', 'fa-exclamation')
        }

        this.distribution.title = this.distribution.title.trim()

        this.setSingleTitleOnSong()
      }
    },
    hasReleaseTypeInTitle: function () {
      const releaseTypeKeywords = BLACKLIST_WORDS
      // Convert to lowercase for case-insensitive matching
      const titleWords = this.distribution.title ? this.distribution.title.toLowerCase().split(/\s+/) : ''
      // Check if any release type keyword is present in the title
      return releaseTypeKeywords.some(keyword => titleWords.includes(keyword.toLowerCase()))
    },
    hasSpecialCaractersInTitle: function () {
      return (this.distribution.title && /[!"/=#%¡¿*+;:_°´¨~`¬⊂･ω?^${}|[\]\\]/g.test(this.distribution.title) && this.distribution.title === '-')
    },
    showInvalidWordsModal: function () {
      window.swal({
        title: this.$t('distribution_process.invalid_word'),
        text: this.$t('distribution_process.album_ep_error_msg'),
        closeOnEsc: false,
        closeOnClickOutside: false,
        className: 'icon-modal',
        icon: 'warning',
        buttons: {
          confirm: {
            text: this.$t('i_understand'),
            className: 'btn btn-warning'
          }
        }
      })
    },
    checkIntroKeyInputTitle: function (e) {
      let char = String.fromCharCode(e.keyCode)

      if (!/[!"/=#%¡¿*+;:_°´¨~`¬⊂･ω?^${}|[\]\\]/g.test(char) && char !== '-') return true
      else e.preventDefault()
    },
    validateBannedWords: function () {
      const regexp = new RegExp(`\\b${this.distroBannedWords.join('|')}\\b`, 'gi')
      let isInvalid = false

      if (this.distribution.title) {
        if (this.distribution.title.match(regexp)) {
          this.distribution.title = this.distribution.title.split(regexp)[0].trim()
          isInvalid = true
        }
      }

      return isInvalid
    },
    invalidBannedWords: function () {
      window.swal({
        title: this.$t('distribution_process.invalid_word'),
        text: `${this.$t('distribution_process.banned_word_error_msg').replaceAll('BANNED_WORDS', this.distroBannedWords.join(', '))}`,
        closeOnEsc: false,
        closeOnClickOutside: false,
        className: 'icon-modal',
        icon: 'warning',
        buttons: {
          confirm: {
            text: this.$t('i_understand'),
            className: 'btn btn-warning'
          }
        }
      })
    },
    validateArtistInDistroTitle: function () {
      let currentArtistName = this.currentArtist ? this.currentArtist.text : ''
      let regExp = new RegExp(currentArtistName.toLowerCase(), 'gi')
      let isInvalid = false

      if (this.distribution.title) {
        if (this.distribution.title.match(regExp)) {
          this.distribution.title = this.distribution.title.toLowerCase().replace(regExp, '').trim()
          isInvalid = true
        }
      }

      return isInvalid
    },
    invalidArtistInDistroTitle: function () {
      window.swal({
        title: this.$t('distribution_process.invalid_word'),
        text: `${this.$t('error_artist_name_in_title')}`,
        closeOnEsc: false,
        closeOnClickOutside: false,
        className: 'icon-modal',
        icon: 'warning',
        buttons: {
          confirm: {
            text: this.$t('i_understand'),
            className: 'btn btn-warning'
          }
        }
      })
    },
    hasReopeningReason: function () {
      return (this.distribution.status === 0 && this.reopeningOptions.includes(this.distribution.reopening_reason))
    },
    validateStrikes: function () {
      if (this.user.strike_items && this.user.strike_items.length) {
        User.getCurrentStrikes({ userId: this.user._id }).then((response) => {
          _.each(response.data, (strike) => {
            this.strikesCount += parseInt(strike.strikes)
          })

          if (this.strikesCount >= 2) {
            this.$router.push(`/dashboard`)
          }
        })
      }
    },
    checkUnsentDistro: function (artist) {
      artist = artist || {}

      let data = {
        artist: artist.id,
        status: 0
      }

      if (!this.isPro) {
        Distributions.getAll(data).then((response) => {
          let unsentDistros = []

          if (this.distribution._id) {
            unsentDistros = _.filter(response.data, distro => (distro._id !== this.distribution._id) && (distro.status === 0 && !distro.reopening_reason))
          } else {
            unsentDistros = _.filter(response.data, distro => distro.status === 0 && !distro.reopening_reason)
          }

          if (unsentDistros.length) {
            this.$router.push('/distributions?unsentDistros=1')
          }
        })
      }
    },
    goToDistros: function () {
      return this.$router.push(`/distributions`)
    },
    setSingleTitleOnSong: function () {
      if (this.distribution.release_type === 'single') {
        this.$eventBus.$emit('setSingleTitleOnSong', this.distribution.title)
      }

      this.$eventBus.$emit('setTitleOnCover', this.distribution.title)
    },
    removeSpotifyProfile: function () {
      if (!this.spotifyProfile.id) {
        this.alreadyHaveSpotify = false
      }
      this.spotifyProfile = null
      this.searchSpotifyArtist()
    },
    selectSpotifyArtist: function (artist) {
      if (this.currentArtist.text !== artist.name) {
        window.swal({
          title: this.$t('match_artist_name_spotify'),
          icon: 'info',
          closeOnEsc: false,
          closeOnClickOutside: false,
          buttons: {
            cancel: {
              text: this.$t('cancel'),
              visible: true,
              className: 'btn btn-default'
            },
            confirm: {
              text: this.$t('confirm'),
              className: 'btn btn-success'
            }
          }
        }).then(result => {
          if (result) {
            _.find(this.indiefyArtists, (item, key) => {
              if (item._id === this.currentArtist.id) {
                this.indiefyArtists[key].name = artist.name
                this.artists[key].text = artist.name
                this.$forceUpdate()
              }
            })
            this.spotifyProfile = artist
          }
          window.$(this.$refs.spotifyModal).modal('hide')
        })
      } else {
        this.spotifyProfile = artist
        window.$(this.$refs.spotifyModal).modal('hide')
      }
    },
    searchSpotifyArtist: function (urlSearch = false) {
      this.spotifyArtists = []
      this.spotifyPrev = null
      this.spotifyNext = null
      this.loadingSpotifyArtists = true

      Artists.checkArtistName(this.currentArtist.text, urlSearch).then(response => {
        if (response.data.results.length) {
          response.data.results.sort(function (a, b) {
            if (a.followers.total > b.followers.total) { return -1 }
            if (a.followers.total < b.followers.total) { return 1 }
            return 0
          })
          this.spotifyPrev = response.data.previous
          this.spotifyNext = response.data.next
          this.spotifyArtists = response.data.results
        }

        this.loadingSpotifyArtists = false
      }).catch(e => {
        this.loadingSpotifyArtists = false
      })

      window.$(this.$refs.spotifyModal).modal('show')
    },
    findUpcExist: function () {
      if (!this.distribution.reopening_reason) {
        if (this.distribution.upc !== '' && barcoder.validate(this.distribution.upc)) {
          Upc.get(this.distribution.upc, this.distribution._id).then(response => {
            if (response.data.success) {
              this.hasErrorUpc = true
              this.$notify({ message: this.$t('upc_already_in_use') }, 'warning', 'fa-exclamation')
              this.distribution.upc = ''
              setTimeout(() => {
                this.hasErrorUpc = false
              }, 1500)
            }
          })
        } else {
          this.hasErrorUpc = true
          this.$notify({ message: this.$t('upc_not_valid') }, 'warning', 'fa-exclamation')
          this.distribution.upc = ''
          setTimeout(() => {
            this.hasErrorUpc = false
          }, 1500)
        }
      }
    },
    changeMetadata: function () {
      if (this.fromMetadataPage) {
        window.swal({
          title: this.$t('are_you_sure'),
          buttons: {
            cancel: {
              visible: true,
              text: this.$t('cancel'),
              className: 'btn btn-default btn-border no-border'
            },
            confirm: {
              text: this.$t('ok'),
              className: 'btn btn-primary'
            }
          }
        }).then(confirm => {
          if (!confirm) {
            return
          }
          this.saveDistribution()
        })
      }
    },
    revertMetadata: function () {
      if (this.fromMetadataPage) {
        window.swal({
          title: this.$t('are_you_sure'),
          buttons: {
            cancel: {
              visible: true,
              text: this.$t('cancel'),
              className: 'btn btn-default btn-border no-border'
            },
            confirm: {
              text: this.$t('ok'),
              className: 'btn btn-primary'
            }
          }
        }).then(confirm => {
          if (!confirm) {
            return
          }
          this.loadingMetadata = true
          Distributions.revertChangesMetadaRequest(this.distribution._id, this.name).then(response => {
            this.distribution = JSON.parse(JSON.stringify(this.distributionOriginal))
            setTimeout(() => {
              this.distribution.style = JSON.parse(JSON.stringify(this.distributionOriginal)).style
            }, 100)
            this.distribution.artist = this.artistOriginal._id
            this.metadataRequestData[this.name] = {}
            this.enableMetadata = !!_.find(this.metadataRequestData[this.name], elem => elem)
            this.metadataChangeClass = {}
            this.$eventBus.$emit('notifyMetadataModified', {origin: this.name, data: this.metadataRequestData[this.name]})
            this.$notify({ message: this.$t('changes_saved') }, 'success', 'fa-check')
            this.loadingMetadata = false
            this.$forceUpdate()
          })
        })
      }
    },
    selectService: function (service) {
      this.saving = true
      this.distribution.service = service._id
      this.activeService = service
      new Promise(resolve => {
        if (this.distribution._id) {
          ServicesOnDemand.setServicetoDistro(service._id, this.distribution._id).then(response => {
            ServicesOnDemand.getServicesBy('extra_distro').then(response => {
              if (response.data.length) {
                this.extraDistroServices = response.data
              }
              resolve()
            })
          })
        } else {
          resolve()
        }
      }).then(() => {
        this.saving = false
        this.$notify({ message: this.$t('service_selected') }, 'success', 'fa-check')
        window.$(this.$refs.extraDistroServicesModal).modal('hide')
      })
    },
    closeExtraDistroServicesModal: function () {
      this.activeService = {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      }
      this.$notify({ message: this.$t('you_have_to_select_service') }, 'warning', 'fa-exclamation')
    },
    deleteService: function (id) {
      this.saving = true
      this.activeService = {
        _id: '',
        total: 0,
        payment_method: '',
        service_type: {},
        paidAt: ''
      }
      new Promise(resolve => {
        if (this.distribution._id) {
          ServicesOnDemand.deleteServiceFromDistro(id, this.distribution._id).then(response => {
            ServicesOnDemand.getServicesBy('extra_distro').then(response => {
              if (response.data.length) {
                this.extraDistroServices = response.data
              }
              resolve()
            })
          })
        } else {
          resolve()
        }
      }).then(() => {
        this.saving = false
        this.$notify({ message: this.$t('you_have_to_select_service') }, 'warning', 'fa-exclamation')
        window.$(this.$refs.extraDistroServicesModal).modal('show')
      })
    },
    initializeDistribution: function () {
      let promise1 = null
      let promise2 = null
      let promise3 = null

      promise1 = new Promise((resolve, reject) => {
        Artists.getAll().then(response => {
          resolve(response)
        })
      })

      promise2 = new Promise((resolve, reject) => {
        User.getNow().then(response => {
          resolve(response)
        })
      })

      promise3 = new Promise((resolve, reject) => {
        Configuration.get({fields: ['end_year_suspend', 'rise_limit_distro', 'distro_banned_words']}).then(response => {
          resolve(response)
        })
      })

      Promise.all([promise1, promise2, promise3]).then(responses => {
        this.indiefyArtists = responses[0].data
        this.artists = _.map(responses[0].data, item => {
          return {
            id: item._id,
            text: item.name,
            spotify_id: item.spotify_id,
            cover_image_small: item.cover_image_small || item.cover_image
          }
        })
        this.distroBannedWords = responses[2].data.distro_banned_words

        new Promise((resolve, reject) => {
          if (this.$route.params.id) {
            Distributions.get(this.$route.params.id).then(response => {
              if (response.data.artist) {
                response.data.artist = response.data.artist._id
              }

              if (this.fromMetadataPage) {
                this.artistOriginal = response.data.artist
              }

              this.distribution = response.data
              if (this.fromMetadataPage) {
                this.distributionOriginal = JSON.parse(JSON.stringify(response.data))
              }
              this.distribution.source = 'web_v2'

              if (!this.fromMetadataPage) {
                if (this.distribution.status > 0) {
                  if (this.$route.params.id) {
                    return this.$router.push(`/distributions/${this.$route.params.id}/details`)
                  } else {
                    return this.$router.push(`/distributions`)
                  }
                } else if (this.hasReopeningReason()) {
                  if (!this.distribution.reopen_in_progress) {
                    return this.$router.push(`/distributions/${this.$route.params.id}/reopening-details`)
                  }
                }
              }

              this.loading = false
              this.endYearSuspend = responses[2].data.end_year_suspend
              this.serverDefaultDate = moment.utc(responses[1].data.now_iso)
              this.initPickers(responses[1].data.now)
              this.riseLimitDistro = responses[2].data.rise_limit_distro

              setTimeout(() => {
                window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
                  on: this.$t('yes'),
                  off: this.$t('no'),
                  width: 80
                })
                if (this.distribution.previously_published) {
                  this.prevPublish = this.distribution.previously_published || 0
                  window.$(`#prevPublish`).prop('checked', true).change()
                }
              }, 100)

              _.each(this.distribution.songs, (song, index) => {
                song.language_data = _.find(this.performanceLanguages, (language) => song.performance_language === language.id)
              })

              if (!_.find(this.genresSource[this.distribution.main_genre], subgenre => subgenre === this.distribution.style)) {
                this.distribution.style = this.distribution.main_genre
              }

              resolve()
            })
          } else if (this.artists[0]) {
            this.validateStrikes()
            // this.checkUnsentDistro(this.artists[0])
            this.loading = false
            setTimeout(() => {
              Vue.set(this.distribution, 'artist', this.artists[0].id)
              window.$('.toggle-checkbox[data-toggle]').bootstrapToggle({
                on: this.$t('yes'),
                off: this.$t('no'),
                width: 80
              })
              this.initPickers(responses[1].data.now)
            }, 1)

            resolve()
          } else {
            this.validateStrikes()
            // this.checkUnsentDistro(this.artists[0])
            this.loading = false
            this.initPickers(responses[1].data.now)
            resolve()
          }
        }).then(() => {
          Distributions.getAvailableDistributions().then(response => {
            if (response.data.available <= 0) {
              ServicesOnDemand.getServicesBy('extra_distro').then(response => {
                if (response.data.length) {
                  this.extraDistroServices = response.data
                  ServicesOnDemand.getServiceByDistro(this.distribution._id, 'extra_distro').then(responseService => {
                    if (responseService.data) {
                      this.activeService = responseService.data
                      this.distribution.service = this.activeService._id
                    } else {
                      window.$(this.$refs.extraDistroServicesModal).modal('show')
                    }
                  })
                } else {
                  if (!this.$route.params.id && !this.fromMetadataPage) {
                    Public.getLocationSettings().then(responseSettings => {
                      this.locationSettings = responseSettings.data

                      if (this.locationSettings.hide_basic && !this.user.subscription) {
                        return this.$router.push(`/upgrade?from=location_limit_editor`)
                      } else {
                        window.$(this.$refs.modalOnDemandServices).modal('show')
                      }
                    })
                  } else {
                    window.$(this.$refs.modalOnDemandServices).modal('show')
                  }
                }
              })
            }

            if (this.fromMetadataPage) {
              this.distributionOriginal.artist = JSON.parse(JSON.stringify(this.artistOriginal))

              if (!this.distributionOriginal.metadata_request_data || !this.distributionOriginal.metadata_request_data[this.name]) {
                this.metadataRequestData[this.name] = {}
              } else {
                this.metadataRequestData[this.name] = this.distributionOriginal.metadata_request_data[this.name]
                _.each(this.metadataRequestData[this.name], (elem, key) => {
                  this.distribution[key] = elem
                  this.metadataChangeClass[key] = true
                })
              }
              this.enableMetadata = !!_.find(this.metadataRequestData[this.name], elem => elem)
            }
          })
        })
      })
    },
    handleInput: function (fromSourceComponent) {
      let finalLanguage = LANGUAGE_REGEX[this.distribution.language.toLowerCase()]
      // Remove the last character (']') from finalLanguage.source and add the ()[]/.,& symbols and numbers
      finalLanguage = new RegExp(`${finalLanguage.source.slice(0, -1)}()\\[\\]/.,&:\\d]`, 'g')

      if (fromSourceComponent === 'release_title') {
        this.distribution.title = this.distribution.title.replace(finalLanguage, '')
      }
    },
    cancelManualSpotifyId () {
      this.showManualSpotifyId = false
      this.manualSpotifyId = ''
      this.manualSpotifyIdError = ''
      this.manualSpotifyIdSuccess = ''
    },
    async submitManualSpotifyId () {
      let id = this.manualSpotifyId.trim()
      let artistId = null

      // Check for Spotify URI
      let uriMatch = id.match(/^spotify:artist:([a-zA-Z0-9]{22})$/)
      if (uriMatch) {
        artistId = uriMatch[1]
      }

      // Check for Spotify URL
      let urlMatch = id.match(/^https?:\/\/open\.spotify\.com\/artist\/([a-zA-Z0-9]{22})(\?.*)?$/)
      if (urlMatch) {
        artistId = urlMatch[1]
      }

      // Check for plain ID (must be exactly 22 alphanumeric chars, nothing else)
      let idMatch = id.match(/^[a-zA-Z0-9]{22}$/)
      if (idMatch) {
        artistId = id
      }

      if (!artistId) {
        this.manualSpotifyIdError = 'invalid_spotify_artist_id'
        this.manualSpotifyIdSuccess = ''
        return
      }

      // Check if the Spotify ID is already used
      try {
        const response = await Artists.getSpotifyArtistIndiefy(artistId)
        if (response.data.duplicated) {
          this.manualSpotifyIdError = 'spotify_artist_duplicated'
          this.manualSpotifyIdSuccess = ''
          return
        }
      } catch (e) {
        this.manualSpotifyIdError = 'error_checking_spotify_id'
        this.manualSpotifyIdSuccess = ''
        return
      }

      this.artist.spotify_id = 'spotify:artist:' + artistId
      this.spotifyProfile = {
        id: artistId,
        uri: 'spotify:artist:' + artistId,
        name: '',
        new: false,
        images: [],
        cover_image_small: ''
      }
      this.manualSpotifyIdError = ''
      this.manualSpotifyIdSuccess = 'spotify_artist_saved'
      setTimeout(() => {
        this.cancelManualSpotifyId()
        window.$(this.$refs.spotifyModal).modal('hide')
      }, 1200)
    }
  },
  mounted: function () {
    if (this.user.custom_label) {
      Labels.getAll().then(response => {
        this.labels = _.map(response.data, item => {
          return { id: item._id, text: item.name }
        })

        if (!this.distribution.custom_label) {
          setTimeout(() => {
            Vue.set(this.distribution, 'custom_label', this.labels[0].id)
          }, 1)
        }
      })
    }

    Public.getLocationSettings().then(responseSettings => {
      this.locationSettings = responseSettings.data

      if (this.locationSettings.hide_basic && !this.user.subscription) {
        return this.$router.push(`/upgrade?from=location_limit_editor`)
      }

      this.initializeDistribution()
    })

    // if (!this.user.subscription) {
    //   setTimeout(() => {
    //     (window.adsbygoogle = window.adsbygoogle || []).push({})
    //   }, 1000)
    // }

    window.scrollTo(0, 0)
  },
  created: function () {
    this.user = this.$user
    this.lang = this.$i18n.locale
    Configuration.get({fields: ['reopen_reasons_fields']}).then(response => {
      this.reopeningOptions = _.map(response.data.reopen_reasons_fields, (elem, key) => key)
    })

    if (this.user.freeze) {
      this.$notify({ message: this.$t('freeze_user_message') }, 'warning', 'fa-exclamation')
      return this.$router.push('/distributions')
    }

    if (!this.$user.subscription) {
      this.isPro = false
    } else {
      this.isPro = true
    }

    if (this.$route.query.verif_send) {
      this.$notify({ message: this.$t('verification_sent') }, 'success', 'fa-check')
    }

    this.metadataRequestData[this.name] = {}
  },
  components: {
    Select2,
    DistributionProgress,
    UpgradeModal
  },
  watch: {
    'distribution.title': function (title) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}

        if (title) {
          if (this.distributionOriginal.title !== title) {
            this.metadataRequestData[this.name].title = title
          } else {
            delete this.metadataRequestData[this.name].title
          }
          notifyData.data = this.metadataRequestData[this.name]
          this.metadataChangeClass['title'] = !!this.metadataRequestData[this.name].title
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.language': function (language) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}

        if (language) {
          if (this.distributionOriginal.language !== language) {
            this.metadataRequestData[this.name].language = language
          } else {
            delete this.metadataRequestData[this.name].language
          }
          notifyData.data = this.metadataRequestData[this.name]
          this.metadataChangeClass['language'] = !!this.metadataRequestData[this.name].language
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.upc': function (upc) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}

        if (upc) {
          if (this.distributionOriginal.upc !== upc) {
            this.metadataRequestData[this.name].upc = upc
          } else {
            delete this.metadataRequestData[this.name].upc
          }
          notifyData.data = this.metadataRequestData[this.name]
          this.metadataChangeClass['upc'] = !!this.metadataRequestData[this.name].upc
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.main_genre': function (mainGenre) {
      if (_.find(this.genresSource[mainGenre], subgenre => subgenre === mainGenre)) {
        if (!_.find(this.genresSource[mainGenre], subgenre => subgenre === this.distribution.style)) {
          this.distribution.style = ''
        }
      }
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}

        if (mainGenre) {
          if (this.distributionOriginal.main_genre !== mainGenre) {
            this.metadataRequestData[this.name].main_genre = mainGenre
          } else {
            delete this.metadataRequestData[this.name].main_genre
          }
          notifyData.data = this.metadataRequestData[this.name]
          this.metadataChangeClass['main_genre'] = !!this.metadataRequestData[this.name].main_genre
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.style': function (style) {
      if (this.fromMetadataPage) {
        let notifyData = {origin: this.name}

        if (style) {
          if (this.distributionOriginal.style !== style) {
            this.metadataRequestData[this.name].style = style
          } else {
            delete this.metadataRequestData[this.name].style
          }
          notifyData.data = this.metadataRequestData[this.name]
          this.metadataChangeClass['style'] = !!this.metadataRequestData[this.name].style
        }
        this.$eventBus.$emit('notifyMetadataChanges', notifyData)
      }
    },
    'distribution.artist': function (currentArtistId, oldArtistId) {
      this.spotifyProfile = null
      this.alreadyHaveSpotify = false
      window.$(this.$refs.spotifyArtist).prop('checked', false).change()

      if (currentArtistId) {
        this.currentArtist = _.find(this.artists, (artist) => artist.id === currentArtistId)

        if (this.currentArtist && this.currentArtist.spotify_id) {
          let spotifyId = this.currentArtist.spotify_id.replace('spotify:artist:', '')

          Artists.getSpotifyArtist(spotifyId).then(response => {
            this.spotifyProfile = response.data
            this.alreadyHaveSpotify = true

            if (this.spotifyProfile) {
              window.$(this.$refs.spotifyArtist).prop('checked', true).change()
            }
          })
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
#distribution-image {
  width: 200px;
  height: 200px;
  border-radius: 3px;
}

.tooltip-content{
  h4 {
    font-size: 14px;
  }
  p {
    font-size: 12px;
    line-height: 20px;
    margin-top: 15px;
  }
}

.title-instructions-modal {
  font-size: 80px
}

.nav-pills .nav-link {
  padding: 10px 0px;
  font-size: 13px;
}

.input-group-append{
  .btn {
    line-height: 17px;
  }
}

.badge-store {
  position: absolute;
  left: -6px;
  top: -6px;
}

.profile-selector {
  border: dashed 2px #a5a5a5;
  cursor: pointer;
  color: #444;
  transition: all ease 0.3s;
  animation-duration: 0.8s;
  &:hover {
    color: #7d7d7d  !important;
    border-color: #e6e5e5 !important;
  }
}

.container-adptable {
  width: 75%;
}

.container-full {
  width: 100%;
}

.show-mobileD {
  display: none;
}

.enable-spotify {
  position: relative;
  .mask {
    background: #f2f5f778;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
  }
}

.metadata-modified {
  box-shadow: 0px 0px 2px 0px #5714ff;
}

.services-table {
  td, th {
    padding: 0 5px !important;
    &.payment-method {
      text-transform: capitalize;
    }
  }
}

.payment-method {
  text-transform: capitalize;
}

.container-service {
  .title {
    display: inline-block;
    padding-top: 6px;
  }
}

.spotify-paginator {
  .paginator {
    width: 49%;
    display: inline-block;
    &.previous {
      text-align: left;
      .btn {
        color: #5714ff !important;
      }
    }
    &.next {
      text-align: right;
      .btn {
        color: #5714ff !important;
      }
    }
  }
}

@media screen and (max-width: 394px) {
  .container-adptable {
    width: 100%;
  }
  .hide-mobileD {
    display: none;
  }
  .show-mobileD {
    display: block;
  }
  .new-artist {
    width: 100%;
    display: grid;
    margin-top: 15px;
    a {
      border-radius: 5px !important;
    }
  }
}
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/DistributionEditor.vue